import { apiClient } from './client';
import { ApiResponse } from '@/types';

export type InspectionStatus = 'pending_agreement' | 'in_progress' | 'completed' | 'archived';
export type FindingSeverity = 'safety_concern' | 'recommendation' | null;
export type FindingSource = 'manual' | 'voice' | 'photo';

// IN/NI/NP — O (observations) is derived at read time from findings count and never stored.
export type ComponentInspectionStatus = 'inspected' | 'not_inspected' | 'not_present' | null;

export interface FindingPhoto {
  id: string;
  url: string;
  storageKey: string;
  caption: string | null;
  createdAt: string;
}

/** Bounding-box annotation produced by AI vision analysis.
 *  Coordinates are fractions 0–1 relative to image dimensions (origin top-left).
 *  DB stores integers ×10000; the backend converts on the way out. */
export interface ComponentPhotoAnnotation {
  id: string;
  componentPhotoId: string;
  findingId: string;
  type: 'rect' | 'arrow' | 'oval';
  /** DB integers ×10000 — divide by 10000 before passing to BBoxAnnotationOverlay */
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Clockwise rotation degrees (0–89) for rect and oval shapes */
  rotationDeg: number;
  createdAt: string;
}

export interface ComponentPhoto {
  id: string;
  url: string;
  storageKey: string;
  caption: string | null;
  createdAt: string;
  /** AI-generated bounding-box annotations linked to this photo. Present on GET; may be empty. */
  annotations: ComponentPhotoAnnotation[];
}

/** Three confidence tiers for displaying AI certainty in the UI.
 *  Derived from the raw 0–1 float returned by the model. */
export type ConfidenceTier = 'high' | 'medium' | 'low';

/** Maps the raw 0–1 model confidence onto a display tier. */
export function confidenceTier(confidence: number): ConfidenceTier {
  if (confidence >= 0.85) return 'high';
  if (confidence >= 0.70) return 'medium';
  return 'low';
}

/** Augments a Finding with the per-finding confidence from the analysis run.
 *  Only present immediately after analyzeComponentPhoto — not persisted or
 *  returned by GET (we log the raw float in vision_analysis_log instead). */
export interface FindingWithConfidence extends Finding {
  confidence: number;
}

export interface Finding {
  id: string;
  roomId: string;
  inspectionId: string;
  componentId: string | null;
  title: string;
  description: string | null;
  severity: FindingSeverity;
  source: FindingSource;
  voiceAnalysisId: string | null;
  isIncluded: boolean;
  /** AI-generated finding the inspector hasn't yet accepted or edited.
   *  Inspector-only — drafts never reach the client portal or PDF and are
   *  excluded from the inspection's published report and list aggregates. */
  aiDraft: boolean;
  photos: FindingPhoto[];  // present from GET; absent from PATCH responses (preserve from state)
  createdAt: string;
  updatedAt: string;
}

export type AttributeFieldType = 'select' | 'text' | 'number';

export interface ComponentAttribute {
  id: string;
  componentId: string;
  key: string;
  value: string | null;
  orderIndex: number;
  /** Schema-derived metadata. Null for custom facts with no matching schema. */
  type: AttributeFieldType;
  /** Human-readable label from schema; falls back to key display when null. */
  label: string | null;
  options: string[] | null;
  unit: string | null;
}

export type ComponentCommentKind = 'note' | 'limitation';
export type ComponentCommentSource = 'template' | 'custom';

export interface ComponentComment {
  id: string;
  componentId: string;
  kind: ComponentCommentKind;
  body: string;
  /** 'template' = snapshotted from the template; 'custom' = added during this inspection. */
  source: ComponentCommentSource;
  /** Inspector can uncheck a templated comment without deleting it. */
  isIncluded: boolean;
  orderIndex: number;
  createdAt: string;
  updatedAt: string;
}

export interface RoomComponent {
  id: string;
  roomId: string;
  name: string;
  inspectionStatus: ComponentInspectionStatus;
  orderIndex: number;
  attributes: ComponentAttribute[];
  comments: ComponentComment[];
  photos: ComponentPhoto[];
}

export interface Room {
  id: string;
  inspectionId: string;
  name: string;
  orderIndex: number;
  condition?: string | null;
  createdAt: string;
  findings: Finding[];
  components: RoomComponent[];
}

export interface Inspection {
  id: string;
  clerkId: string;
  propertyAddress: string;
  clientName: string | null;
  clientEmail: string | null;
  inspectionDate: string;
  status: InspectionStatus;
  notes: string | null;
  findingCount?: number;
  coverPhotoUrl?: string;
  /**
   * Set when the inspection was created from a scheduled appointment (#53).
   * Drives the agreement-status surface on the inspection page (#79).
   */
  appointmentId?: string | null;
  createdAt: string;
  updatedAt: string;
  rooms: Room[];
}

export interface InspectionSummary {
  id: string;
  propertyAddress: string;
  clientName: string | null;
  clientEmail: string | null;
  inspectionDate: string;
  status: InspectionStatus;
  appointmentId?: string | null;
  findingCount: number;
  /**
   * 2-value severity split for the list card. Optional because older API
   * responses (and tests) may not include them — treat absence as 0.
   */
  safetyCount?: number;
  recommendationCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInspectionParams {
  property_address: string;
  client_name?: string;
  client_email?: string;
  inspection_date?: string;
  /**
   * Optional template to snapshot for this inspection. When omitted the
   * backend uses the inspector's default template (promoting/seeding as
   * needed). Sent only when the user explicitly picked a non-default
   * template in the New Inspection modal.
   */
  template_id?: string;
}

export interface AddRoomParams {
  name: string;
  order_index?: number;
}

export interface AddFindingParams {
  title: string;
  description?: string;
  severity?: FindingSeverity;
}

export interface UpdateFindingParams {
  title?: string;
  description?: string;
  severity?: FindingSeverity;
  is_included?: boolean;
  /** Only the false transition is meaningful — used by Accept and
   *  Edit-and-save to flip an AI-draft finding into an inspector-authored one. */
  ai_draft?: false;
}

export const inspectionsApi = {
  create: async (params: CreateInspectionParams) => {
    return apiClient.post<ApiResponse<InspectionSummary>>('/inspections', params);
  },

  list: async () => {
    return apiClient.get<ApiResponse<InspectionSummary[]>>('/inspections');
  },

  get: async (id: string) => {
    return apiClient.get<ApiResponse<Inspection>>(`/inspections/${id}`);
  },

  addRoom: async (inspectionId: string, params: AddRoomParams) => {
    return apiClient.post<ApiResponse<Room>>(`/inspections/${inspectionId}/rooms`, params);
  },

  deleteRoom: async (inspectionId: string, roomId: string) => {
    return apiClient.delete<ApiResponse<void>>(`/inspections/${inspectionId}/rooms/${roomId}`);
  },

  updateRoom: async (inspectionId: string, roomId: string, data: { condition?: string }) => {
    return apiClient.patch<ApiResponse<Room>>(`/inspections/${inspectionId}/rooms/${roomId}`, data);
  },

  addFinding: async (inspectionId: string, roomId: string, params: AddFindingParams) => {
    return apiClient.post<ApiResponse<Finding>>(
      `/inspections/${inspectionId}/rooms/${roomId}/findings`,
      params
    );
  },

  updateFinding: async (inspectionId: string, findingId: string, params: UpdateFindingParams) => {
    return apiClient.patch<ApiResponse<Finding>>(
      `/inspections/${inspectionId}/findings/${findingId}`,
      params
    );
  },

  deleteFinding: async (inspectionId: string, findingId: string) => {
    return apiClient.delete<ApiResponse<void>>(
      `/inspections/${inspectionId}/findings/${findingId}`
    );
  },


  addFindingPhoto: async (inspectionId: string, findingId: string, file: File): Promise<FindingPhoto> => {
    const resp = await apiClient.uploadFile<ApiResponse<FindingPhoto>>(
      `/inspections/${inspectionId}/findings/${findingId}/photos`,
      file,
    );
    if (!resp.success || !resp.data) throw new Error('Failed to upload photo');
    return resp.data;
  },

  deleteFindingPhoto: async (inspectionId: string, findingId: string, photoId: string) => {
    return apiClient.delete<ApiResponse<void>>(
      `/inspections/${inspectionId}/findings/${findingId}/photos/${photoId}`
    );
  },

  updateStatus: async (inspectionId: string, status: InspectionStatus) => {
    return apiClient.patch<ApiResponse<InspectionSummary>>(
      `/inspections/${inspectionId}/status`,
      { status }
    );
  },

  generateReport: async (inspectionId: string) => {
    return apiClient.post<ApiResponse<{ reportId: string }>>(
      `/inspections/${inspectionId}/generate-report`,
      {}
    );
  },

  uploadCoverPhoto: async (inspectionId: string, file: File): Promise<string> => {
    const resp = await apiClient.uploadFile<ApiResponse<{ coverPhotoUrl: string }>>(
      `/inspections/${inspectionId}/cover-photo`,
      file,
    );
    if (!resp.success || !resp.data) throw new Error('Failed to upload cover photo');
    return resp.data.coverPhotoUrl;
  },

  deleteCoverPhoto: async (inspectionId: string) => {
    return apiClient.delete<ApiResponse<void>>(`/inspections/${inspectionId}/cover-photo`);
  },

  reorderFinding: async (inspectionId: string, findingId: string, direction: 'up' | 'down') => {
    return apiClient.post<ApiResponse<void>>(
      `/inspections/${inspectionId}/findings/${findingId}/reorder`,
      { direction }
    );
  },

  addComponentFinding: async (
    inspectionId: string,
    roomId: string,
    componentId: string,
    params: AddFindingParams,
  ) => {
    return apiClient.post<ApiResponse<Finding>>(
      `/inspections/${inspectionId}/rooms/${roomId}/components/${componentId}/findings`,
      params,
    );
  },

  addRoomComponent: async (
    inspectionId: string,
    roomId: string,
    params: { name: string; attributes?: Array<{ key: string }> },
  ) => {
    return apiClient.post<ApiResponse<RoomComponent>>(
      `/inspections/${inspectionId}/rooms/${roomId}/components`,
      params,
    );
  },

  setComponentStatus: async (
    inspectionId: string,
    componentId: string,
    status: ComponentInspectionStatus,
  ) => {
    return apiClient.patch<ApiResponse<RoomComponent>>(
      `/inspections/${inspectionId}/components/${componentId}/status`,
      { inspection_status: status },
    );
  },

  addComponentAttribute: async (
    inspectionId: string,
    componentId: string,
    key: string,
    value: string | null,
  ) => {
    return apiClient.post<ApiResponse<ComponentAttribute>>(
      `/inspections/${inspectionId}/components/${componentId}/attributes`,
      { key, value },
    );
  },

  deleteComponentAttribute: async (
    inspectionId: string,
    componentId: string,
    attributeId: string,
  ) => {
    return apiClient.delete<ApiResponse<{ id: string }>>(
      `/inspections/${inspectionId}/components/${componentId}/attributes/${attributeId}`,
    );
  },

  updateComponentAttribute: async (
    inspectionId: string,
    componentId: string,
    attributeId: string,
    value: string | null,
  ) => {
    return apiClient.patch<ApiResponse<ComponentAttribute>>(
      `/inspections/${inspectionId}/components/${componentId}/attributes/${attributeId}`,
      { value },
    );
  },

  // ── inspection-side component comments (notes + limitations) ─────────
  //
  // Templated comments land via snapshot on inspection creation. These
  // routes let the inspector add day-of observations (source='custom')
  // or toggle include/edit body on existing ones.
  addComponentComment: async (
    inspectionId: string,
    componentId: string,
    input: { kind: ComponentCommentKind; body: string },
  ) => {
    return apiClient.post<ApiResponse<ComponentComment>>(
      `/inspections/${inspectionId}/components/${componentId}/comments`,
      input,
    );
  },

  updateComponentComment: async (
    inspectionId: string,
    componentId: string,
    commentId: string,
    patch: { body?: string; is_included?: boolean; order_index?: number },
  ) => {
    return apiClient.patch<ApiResponse<ComponentComment>>(
      `/inspections/${inspectionId}/components/${componentId}/comments/${commentId}`,
      patch,
    );
  },

  deleteComponentComment: async (
    inspectionId: string,
    componentId: string,
    commentId: string,
  ) => {
    return apiClient.delete<ApiResponse<{ id: string }>>(
      `/inspections/${inspectionId}/components/${componentId}/comments/${commentId}`,
    );
  },

  addComponentPhoto: async (
    inspectionId: string,
    roomId: string,
    componentId: string,
    file: File,
  ): Promise<ComponentPhoto> => {
    const resp = await apiClient.uploadFile<ApiResponse<ComponentPhoto>>(
      `/inspections/${inspectionId}/rooms/${roomId}/components/${componentId}/photos`,
      file,
    );
    if (!resp.success || !resp.data) throw new Error('Failed to upload photo');
    return resp.data;
  },

  deleteComponentPhoto: async (
    inspectionId: string,
    roomId: string,
    componentId: string,
    photoId: string,
  ) => {
    return apiClient.delete<ApiResponse<void>>(
      `/inspections/${inspectionId}/rooms/${roomId}/components/${componentId}/photos/${photoId}`,
    );
  },

  /**
   * Fetch (or generate) the AI attribute schema for a given component type.
   * Library-as-cache: first call may invoke Haiku; subsequent calls return
   * the cached schema from DB. Safe to call on focus — fast after the first
   * miss.
   */
  getAttributeSchema: async (sectionName: string, componentName: string) => {
    return apiClient.post<ApiResponse<{
      attributes: Array<{ key: string; label: string; type: string; options?: string[]; unit?: string }>;
    }>>('/component-attributes', { sectionName, componentName });
  },

  /** Trigger AI vision analysis on a component photo.
   *  Returns the created draft findings (up to 3) with per-finding confidence,
   *  and the bounding-box annotations linked to this photo.
   *
   *  Idempotent: calling twice for the same photoId returns the existing results. */
  analyzeComponentPhoto: async (
    inspectionId: string,
    roomId: string,
    componentId: string,
    photoId: string,
  ): Promise<{ findings: FindingWithConfidence[]; annotations: ComponentPhotoAnnotation[] }> => {
    const resp = await apiClient.post<ApiResponse<{
      findings: FindingWithConfidence[];
      annotations: ComponentPhotoAnnotation[];
    }>>(
      `/inspections/${inspectionId}/rooms/${roomId}/components/${componentId}/photos/${photoId}/analyze`,
      {},
    );
    if (!resp.success || !resp.data) {
      throw new Error((resp as { error?: string }).error ?? 'Failed to analyze photo');
    }
    return resp.data;
  },
};
